import { Box, Chip } from "@mui/material";
import { settings } from "app";
import {
  ChipField,
  Datagrid,
  FunctionField,
  NumberField,
  ReferenceArrayField,
  ReferenceField,
  SelectField,
  SingleFieldList,
  TextField,
  useRecordContext,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { NationalityType } from "utils/choices";
import RowNumberField from "server/components/Fields/RowNumberField";
import NestedItems from "./NestedItems";
import RowActions from "./RowActions";
import { useCollectionActions } from "./CollectionContext";
import { useCurriculum } from "../CurriculumContext";
import CreditFields from "../forms/CreditFields";
import ScheduleFields from "../forms/ScheduleFields";

const DisciplineExpand = () => {
  const row = useRecordContext();
  const { items, saving, updateNestedAt } = useCollectionActions();

  if (!row) return null;

  const item = items[row.rowIndex];

  return (
    <Box sx={{ p: 1 }}>
      <NestedItems
        title="Кредити"
        singular="кредити"
        rowPrefix={`disciplines#${row.rowIndex}.credits`}
        items={item?.credits}
        FormFields={CreditFields}
        saving={saving}
        onSave={next => updateNestedAt(row.rowIndex, "credits", next)}
      >
        <SelectField
          source="nationality"
          choices={NationalityType}
          label="Националност"
          sortable={false}
          emptyText="-"
        />
        <ReferenceField
          source="instructionLanguageId"
          reference={administrationRequests.getInstructionLanguagesSelect()}
          label="Език на преподаване"
          link={false}
          sortable={false}
          emptyText="-"
        >
          <TextField
            source="name"
            emptyText="-"
          />
        </ReferenceField>
        <NumberField
          source="credits"
          label="Кредити"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
      </NestedItems>

      <NestedItems
        title="Разписание"
        singular="разписание"
        rowPrefix={`disciplines#${row.rowIndex}.schedules`}
        items={item?.schedules}
        FormFields={ScheduleFields}
        formProps={{ finalExamInSemester: item?.finalExamInSemester }}
        saving={saving}
        onSave={next => updateNestedAt(row.rowIndex, "schedules", next)}
      >
        <NumberField
          source="semester"
          label="Семестър"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
        <NumberField
          source="lecturesPerSemester"
          label="Лекции / семестър"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
        <NumberField
          source="seminarExercisesPerSemester"
          label="СУ / семестър"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
        <NumberField
          source="laboratoryExercisesPerSemester"
          label="ЛУ / семестър"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
        <NumberField
          source="practicalExercisesPerSemester"
          label="ПУ / семестър"
          sortable={false}
          textAlign="left"
          emptyText="-"
        />
      </NestedItems>
    </Box>
  );
};

const DisciplinesGrid = ({ collection }) => {
  const curriculum = useCurriculum();

  return (
    <Datagrid
      bulkActionButtons={false}
      rowClick={false}
      expandSingle
      expand={<DisciplineExpand />}
      sx={[settings.style.detailsDatagrid, { "& .RaDatagrid-headerCell": { textAlign: "left" } }]}
    >
      <RowNumberField label="№" />
      <ReferenceField
        source="disciplineId"
        reference={administrationRequests.getDisciplinesSelect()}
        label="Дисциплина"
        link={false}
        sortable={false}
        emptyText="-"
      >
        <TextField
          source="name"
          emptyText="-"
        />
      </ReferenceField>
      <ReferenceField
        source="formOfControl"
        reference={studentRequests.getCurriculumsControlFormsSelect()}
        label="Форма на контрол"
        link={false}
        sortable={false}
        emptyText="-"
      >
        <TextField
          source="name"
          emptyText="-"
        />
      </ReferenceField>
      <NumberField
        source="finalExamInSemester"
        label="Изпит в семестър"
        sortable={false}
        textAlign="left"
        emptyText="-"
      />
      <FunctionField
        label="Изпит по време на семестъра"
        sortable={false}
        render={discipline =>
          discipline.examDuringSemester ? (
            <Chip
              label="Да"
              color="success"
              size="small"
            />
          ) : (
            <Chip
              label="Не"
              variant="outlined"
              size="small"
            />
          )
        }
      />
      <NumberField
        source="lecturesAuditoriumOccupancy"
        label="Лекции (ч.)"
        sortable={false}
        textAlign="left"
        emptyText="-"
      />
      <NumberField
        source="seminarExercisesAuditoriumOccupancy"
        label="СУ (ч.)"
        sortable={false}
        textAlign="left"
        emptyText="-"
      />
      <NumberField
        source="laboratoryExercisesAuditoriumOccupancy"
        label="ЛУ (ч.)"
        sortable={false}
        textAlign="left"
        emptyText="-"
      />
      <NumberField
        source="practicalExercisesAuditoriumOccupancy"
        label="ПУ (ч.)"
        sortable={false}
        textAlign="left"
        emptyText="-"
      />
      <NumberField
        source="extracurricularActivities"
        label="Извънаудиторна"
        sortable={false}
        textAlign="left"
        emptyText="-"
      />
      <ReferenceArrayField
        source="moduleIds"
        reference={studentRequests.getCurriculumModulesSelect(curriculum.id)}
        label="Модули"
        sortable={false}
      >
        <SingleFieldList linkType={false}>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <FunctionField
        label=""
        sortable={false}
        render={() => <RowActions collection={collection} />}
      />
    </Datagrid>
  );
};

export default DisciplinesGrid;
